import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Paper, Typography, Card, CardContent, CardActions,
  Button, Chip, LinearProgress, List, ListItem, ListItemText,
  ListItemAvatar, Avatar, Divider
} from '@mui/material';
import {
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Email as EmailIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import API_CONFIG, { getApiUrl } from '../../config/api';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    users: { total: 0, today: 0, thisWeek: 0 },
    surveys: { total: 0, active: 0, draft: 0 },
    responses: { total: 0, today: 0, pending: 0 },
    emails: { queued: 0, sent: 0, failed: 0 }
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch users stats
      const usersRes = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.USER.LIST_USERS_WITH_ONBOARDING), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_password: 'Admin@123!!!' })
      });
      const usersData = await usersRes.json();

      // Fetch surveys stats
      const surveysRes = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.SURVEYS.LIST) + '?limit=1000');
      const surveysData = await surveysRes.json();

      // Fetch email queue stats
      const emailRes = await fetch(getApiUrl('/admin/email-stats'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_password: 'Admin@123!!!' })
      });
      const emailData = await emailRes.json();

      // Process data
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

      const users = usersData.users || [];
      const surveys = surveysData.surveys || [];

      setStats({
        users: {
          total: users.length,
          today: users.filter(u => new Date(u.created_at) >= today).length,
          thisWeek: users.filter(u => new Date(u.created_at) >= weekAgo).length
        },
        surveys: {
          total: surveys.length,
          active: surveys.filter(s => s.status === 'published').length,
          draft: surveys.filter(s => s.status === 'draft').length
        },
        responses: {
          total: 0, // Would need to fetch from responses API
          today: 0,
          pending: 0
        },
        emails: emailData.stats || { queued: 0, sent: 0, failed: 0 }
      });

      // Generate recent activity
      const activity = [
        ...users.slice(0, 5).map(user => ({
          type: 'user',
          message: `New user registered: ${user.email}`,
          time: user.created_at,
          icon: <PeopleIcon color="primary" />
        })),
        ...surveys.slice(0, 3).map(survey => ({
          type: 'survey',
          message: `Survey ${survey.status}: ${survey.name}`,
          time: survey.updated_at,
          icon: <AssignmentIcon color="secondary" />
        }))
      ].sort((a, b) => new Date(b.time) - new Date(a.time));

      setRecentActivity(activity.slice(0, 10));

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, subtitle, icon, color = 'primary', onClick }) => (
    <Card sx={{ height: '100%', cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ color: `${color}.main`, mr: 2 }}>
            {icon}
          </Box>
          <Typography color="textSecondary" gutterBottom variant="h6">
            {title}
          </Typography>
        </Box>
        <Typography variant="h3" component="div" sx={{ mb: 1 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="textSecondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
      {onClick && (
        <CardActions>
          <Button size="small" color={color}>
            View Details
          </Button>
        </CardActions>
      )}
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 4 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
        Admin Dashboard
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={stats.users.total}
            subtitle={`${stats.users.today} today, ${stats.users.thisWeek} this week`}
            icon={<PeopleIcon sx={{ fontSize: 40 }} />}
            color="primary"
            onClick={() => navigate('/admin/users')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Surveys"
            value={stats.surveys.active}
            subtitle={`${stats.surveys.total} total, ${stats.surveys.draft} drafts`}
            icon={<AssignmentIcon sx={{ fontSize: 40 }} />}
            color="secondary"
            onClick={() => navigate('/admin/surveys')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Email Queue"
            value={stats.emails.queued}
            subtitle={`${stats.emails.sent} sent, ${stats.emails.failed} failed`}
            icon={<EmailIcon sx={{ fontSize: 40 }} />}
            color="success"
            onClick={() => navigate('/admin/emails')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Responses"
            value={stats.responses.total}
            subtitle={`${stats.responses.today} today`}
            icon={<QuestionAnswerIcon sx={{ fontSize: 40 }} />}
            color="warning"
            onClick={() => navigate('/admin/responses')}
          />
        </Grid>
      </Grid>

      {/* Quick Actions and Recent Activity */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<PeopleIcon />}
                onClick={() => navigate('/admin/users')}
                fullWidth
              >
                Manage Users
              </Button>
              <Button
                variant="contained"
                startIcon={<AssignmentIcon />}
                onClick={() => navigate('/admin/surveys')}
                fullWidth
              >
                Manage Surveys
              </Button>
              <Button
                variant="contained"
                startIcon={<EmailIcon />}
                onClick={() => navigate('/admin/emails')}
                fullWidth
              >
                Email Management
              </Button>
              <Button
                variant="contained"
                startIcon={<QuestionAnswerIcon />}
                onClick={() => navigate('/admin/responses')}
                fullWidth
              >
                View Responses
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Activity
            </Typography>
            <List>
              {recentActivity.length > 0 ? (
                recentActivity.map((activity, index) => (
                  <React.Fragment key={index}>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'transparent' }}>
                          {activity.icon}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={activity.message}
                        secondary={new Date(activity.time).toLocaleString()}
                      />
                    </ListItem>
                    {index < recentActivity.length - 1 && <Divider />}
                  </React.Fragment>
                ))
              ) : (
                <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
                  No recent activity
                </Typography>
              )}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboard;